/**
 * Centralized API error toast helper.
 *
 * - ApiError with requestId → persistent red toast + "Copy" button
 * - Other errors → standard toast with error message
 *
 * 🔴 Un INCIDENT (5xx) ne se titre PAS par la phrase du serveur : « Erreur
 * interne du serveur. » s'affichait en FRANÇAIS dans l'interface anglaise, dans
 * les 269 fichiers qui appellent ce toast (TRANSFERT/dette/0). Le titre est la
 * phrase d'incident de la table transverse (`phraseDIncident`, décidée par le
 * STATUT, `estUnIncident`) ; la référence reste dans la description, avec
 * « Copier ». Une coupure réseau (`TypeError`, « Failed to fetch » dans la
 * langue du NAVIGATEUR) se dit par `common.networkError`, comme partout.
 *
 * 🔴 Un REFUS TRANSVERSE (droit manquant, compte d'espace externe, entité hors
 * périmètre, borne de saisie, élément introuvable…) se titrait lui aussi par la
 * phrase du serveur : « This account only has access to its portal space » dans
 * l'interface française, « Cette réservation est introuvable. » dans l'anglaise.
 * Il se dit par la table transverse, dans la langue de l'écran (`titreDuRefus`).
 *
 * ⚠️ Une phrase déjà COMPOSÉE par l'écran (`ApiError.phraseDEcran` : les portes du
 * coffre, l'export de paie) reste le titre : la relire par son code la remplaçait
 * par la phrase générique de sa famille (« The parent folder no longer exists… »
 * devenait « This item no longer exists… »). Et une erreur de module qui sait se
 * dire sous la forme canonique (`enApiError()`, celle du devis) suit la même règle
 * qu'une `ApiError` (`sousFormeCanonique`).
 */

import { toast } from 'sonner'
import i18n from '@/i18n'
import { ApiError, supportCodeOf } from './api-error'
import {
    codeDuRefusTransverse,
    entreesDeValidation,
    estUnIncident,
    phraseDeFamille,
    phraseDeRefusTransverse,
    phraseDIncident,
} from './api-error-transverse'
import {
    langueDeLInterface,
    phraseDeSaisiePourLInterface,
    phraseDuServeurPourLInterface,
    phraseRedigeeParLeServeur,
} from './phraseDuServeur'

/**
 * Le titre du toast d'une `ApiError`. L'ordre est la règle :
 *
 * ① une PANNE (5xx) : la phrase d'incident ;
 * ② une phrase déjà COMPOSÉE par l'écran (`phraseDEcran`) : telle quelle ;
 * ③ un refus que la table ignore : la phrase que le serveur a rédigée, sinon le repli de l'écran
 *   — jamais le repli technique d'un lecteur (« API error 409 », « Failed to create folder: 429 »,
 *   « Erreur 404 » : `REPLI_TECHNIQUE`), ni un code brut ;
 * ④ un 422 : chaque erreur par `phraseDeSaisiePourLInterface` — la phrase qu'un validateur a
 *   rédigée pour l'interface française, la phrase de la table (type, borne) sinon ;
 * ⑤ un code EXACT de la table : sa phrase, dans toute langue — écrite pour ce refus, quand
 *   celle du serveur est anglaise ou technique (« Permission 'x' required ») ;
 * ⑥ la FAMILLE `*_NOT_FOUND`, phrase GÉNÉRIQUE : pour l'interface française, la phrase précise
 *   que le module a rédigée en français ; pour toute autre, et pour un introuvable que le serveur
 *   écrit encore en anglais (« Email template not found »), la générique traduite
 *   (`phraseDuServeurPourLInterface`, `lib/phraseDuServeur.ts`).
 *   ⛔ Ni une phrase française dans l'interface anglaise, ni « Cet élément n'existe plus » à la
 *   place de « Cette réservation est introuvable. » pour qui lit le français.
 */
function titreDuRefus(err: ApiError, repli: string): string {
    if (estUnIncident(err)) return phraseDIncident(i18n.t)
    if (err.phraseDEcran && err.message) return err.message
    const transverse = phraseDeRefusTransverse(err, i18n.t)
    if (transverse === null) return phraseRedigeeParLeServeur(err) || repli
    const langue = langueDeLInterface(i18n)
    const entrees = entreesDeValidation(err)
    if (entrees.length > 0) return phraseDeSaisiePourLInterface(entrees, i18n.t, langue)
    if (phraseDeFamille(codeDuRefusTransverse(err), i18n.t) === null) return transverse
    return phraseDuServeurPourLInterface(err, langue) || transverse
}

/**
 * L'erreur sous la forme que ce toast lit : une `ApiError`, ou une erreur de module qui sait s'y
 * convertir (`enApiError()` — `QuoteApiError`, le devis). Sans elle, un refus du devis sortait par
 * son `message` : la phrase anglaise du serveur, « API error: 422 », une panne sans référence.
 * ⚠️ Pas de conversion PAR FORME (tout ce qui porte un statut et un code) : d'autres erreurs de
 * module rangent dans `message` une phrase déjà traduite (`AutoSaveError`…), qu'une relecture par
 * le code remplacerait. Une erreur s'inscrit ici en déclarant `enApiError()`.
 */
function sousFormeCanonique(err: unknown): unknown {
    const convertir = (err as { enApiError?: unknown } | null | undefined)?.enApiError
    return typeof convertir === 'function' ? convertir.call(err) : err
}

/**
 * Show a toast for an API error.
 *
 * - **Erreur métier anticipée** (4xx avec code explicite, ex. SIRET_DUPLICATE,
 *   CLIENT_ANONYMIZED, CONTACT_NOT_IN_CLIENT…) : toast court avec message en
 *   clair, **pas** de mention support, **pas** de durée infinie.
 * - **Vraie panne** (5xx, ou erreur sans code) : toast persistant avec le
 *   request_id et le bouton "Copier" pour escalader auprès du support.
 */
export function toastApiError(erreur: unknown, fallbackMessage: string) {
    const err = sousFormeCanonique(erreur)
    if (err instanceof ApiError) {
        const titre = titreDuRefus(err, fallbackMessage)
        const requestId = supportCodeOf(err)
        if (requestId) {
            toast.error(titre, {
                duration: Infinity,
                description: i18n.t(
                    'common.errorCodeSupport',
                    'Transmettez ce code au support : {{code}}',
                    { code: requestId },
                ),
                action: {
                    label: i18n.t('common.errorCodeCopy', 'Copier'),
                    onClick: () => navigator.clipboard.writeText(requestId),
                },
            })
            return
        }
        toast.error(titre)
        return
    }
    if (err instanceof TypeError) {
        toast.error(i18n.t('common.networkError'))
        return
    }
    toast.error(err instanceof Error ? err.message : fallbackMessage)
}
